/**
 * 样片墙**嵌入照片线**(IA 设计稿 §3.2「底部」;2026-08-08 P6)。
 *
 * ★为什么嵌进来:样片的价值是「照着这个改」—— 它要在**你正要写文案的那一刻**出现。
 *   今天它是一个独立页 `/showcase`,进了创作页就看不见了;想看得先离开这一页,
 *   看完再点「一键套用」跳回来 —— 一来一回,而且刚写的东西还得担心丢没丢。
 *
 * ★只在照片线出现:样片的 `avatarId` 是**预置形象**(照片资产),recipe 套过来落的就是
 *   照片档。真人口播线套一份预置形象的样片,那条线连「预置形象」这个来源 tab 都没有。
 *
 * ★横滚一条,不做第二个卡墙:这一页的主角是文案框,样片是**参考**不是**目录**。
 *   要挑得仔细,「看全部样片 →」在原地给出口,不把整面墙搬进来跟文案抢版面。
 *
 * ★套用会**覆盖当前文案** → 必须先问一句。用户可能已经写了半天(存量教训:
 *   一个格子超长 422 掉整份产物,4900 积分白干 —— 覆盖用户输入这件事要格外小心)。
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { cn } from "@/shared/lib/utils";
import { dhShowcaseService, toRecipe, type DhShowcaseDto } from "../../api/dhShowcaseService";
import type { DhShowcaseRecipe } from "../../api/dhShowcaseService";

export interface DhShowcaseStripProps {
  /** 当前文案 —— 非空时套用要先问一句(会被覆盖)。 */
  currentScript: string;
  onApply: (r: DhShowcaseRecipe) => void;
  onViewAll: () => void;
}

export function DhShowcaseStrip({ currentScript, onApply, onViewAll }: DhShowcaseStripProps) {
  // 只取一屏的量:这是参考条不是目录,拉全量既慢又没人在这儿翻页。
  const q = useQuery({
    queryKey: ["dh", "showcase", "strip"],
    queryFn: () => dhShowcaseService.gallery({ page: 1, size: 8 }),
    staleTime: 5 * 60_000,
    retry: 1,
    // 失败静默:样片是锦上添花,拉不到就整条不渲染,不在创作页顶上弹红条。
    meta: { suppressGlobalErrorToast: true },
  });
  const items = q.data?.records ?? [];
  const [pending, setPending] = useState<DhShowcaseDto | null>(null);

  // 空态整条不渲染 —— 新租户的创作页不该多一条「样片墙还在准备中」的白条。
  if (items.length === 0) return null;

  const pick = (s: DhShowcaseDto) => {
    if (currentScript.trim())
      setPending(s); // 有文案 → 先问
    else onApply(toRecipe(s));
  };

  return (
    <section className="shrink-0 space-y-1.5" data-testid="dh-showcase-strip">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Sparkles className="h-3.5 w-3.5 self-center text-primary" />
        <span className="text-xs font-medium">照着样片改</span>
        <span className="text-2xs text-muted-foreground">
          平台精选 · 一键把形象 / 音色 / 文案填进来
        </span>
        <button
          type="button"
          onClick={onViewAll}
          className="ml-auto min-h-8 rounded-full px-2 text-2xs font-medium text-primary transition-colors hover:bg-primary/10"
        >
          看全部样片 →
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {items.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`dh-showcase-pick-${s.id}`}
            aria-label={`套用样片 ${s.title}`}
            onClick={() => pick(s)}
            className={cn(
              "flex w-32 shrink-0 flex-col overflow-hidden rounded-lg border bg-card text-left transition-all",
              "hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-md"
            )}
          >
            <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted/40">
              {s.avatarThumbUrl ? (
                <img
                  src={s.avatarThumbUrl}
                  alt={s.avatarLabel}
                  loading="lazy"
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Sparkles className="h-5 w-5 text-muted-foreground/40" />
                </div>
              )}
            </div>
            <span className="truncate px-1.5 pt-1 text-2xs font-medium">{s.title}</span>
            <span className="truncate px-1.5 pb-1 text-2xs text-muted-foreground">
              {s.avatarLabel} · {s.voiceLabel}
            </span>
          </button>
        ))}
      </div>

      {/* ★覆盖用户已写的文案要先问一句 —— 并且把「会被覆盖成什么」说清楚 */}
      <AlertDialog open={pending !== null} onOpenChange={(v) => !v && setPending(null)}>
        <AlertDialogContent data-testid="dh-showcase-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>套用「{pending?.title}」?</AlertDialogTitle>
            <AlertDialogDescription>
              会把<b className="font-medium text-foreground">当前文案、形象、音色、清晰度</b>
              一起换成这份样片的。你现在写的这段会被覆盖,换完可以继续改。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>不换,继续写我的</AlertDialogCancel>
            <AlertDialogAction
              data-testid="dh-showcase-confirm-ok"
              onClick={() => {
                if (pending) onApply(toRecipe(pending));
                setPending(null);
              }}
            >
              套用这份样片
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
